import classNames from "classnames";
import {  Fragment } from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";

import { FillCheckIcon } from "../../SvgIcon";
import { FormLabel } from "../FormLabel";
import { FormError } from "../FormError";

type RadioSelectProps<TFieldValues extends FieldValues, TContext extends object> = {
  id: string;
  name?: FieldPath<TFieldValues>;
  title?: string | React.ReactNode;
  titleClasses?: string;
  subTitle?: string | React.ReactNode;
  subTitleClasses?: string;
  hint?: React.ReactNode;
  classes?: string;
  theme?: "light" | "dark";
  column?: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  type?: "radio" | "card" | "card-brief" | "card-list";
  control: Control<TFieldValues, TContext>;
  onChange?: (val: string) => void;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  error?: string;
  options:
    | string[]
    | { label: string; value: string }[]
    | { label: { img: string; title: string; desc: string }; value: string }[];
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
};

/**
 *
 * @param {*} type i.e. radio/card/card-brief
 * @returns
 */
export function RadioSelect<TFieldValues extends FieldValues, TContext extends object>({
  title,
  titleClasses,
  subTitle,
  subTitleClasses,
  hint,
  id,
  name = id as FieldPath<TFieldValues>,
  control,
  options,
  readOnly,
  disabled,
  classes,
  onChange: _onChange,
  type = "radio",
  column = "2",
  theme = "light"
}: RadioSelectProps<TFieldValues, TContext>) {
  if (type === "radio") {
    return (
      <div className={classes}>
        <div className="mb-2">
          {title ? (
            <FormLabel id={name} htmlFor={name} className={titleClasses}>
              {title}
            </FormLabel>
          ) : null}
          {subTitle ? <p className={`text-sm font-light text-gray-600 ${subTitleClasses}`}>{subTitle}</p> : null}
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, name, ref, value: selectedValue }, fieldState: { error } }) => {
            return (
              <Fragment>
                <div className={`grid grid-cols-${column} gap-5`}>
                  {options.map((option, idx) => {
                    let _label = "";
                    let _value = "";
                    if (typeof option === "object" && typeof option.label === "string") {
                      _label = option.label;
                      _value = option.value.toLowerCase();
                    } else if (typeof option === "string") {
                      _label = option;
                      _value = option.toLowerCase();
                    }

                    const isChecked = _value?.toLowerCase() === selectedValue?.toLowerCase();
                    return (
                      <label
                        key={+idx}
                        className={classNames(
                          "radio-container text-gray-700 text-left leading-snug h-11 flex items-center max-w-sm justify-start px-2 border-2 rounded-[10px] group",
                          {
                            "bg-[#f2f2f2] hover:bg-[#dedede]": theme === "light",
                            "bg-white hover:bg-[#fafafa]": theme === "dark",
                            "border-green-600": isChecked,
                            "border-[#f2f2f2] hover:border-gray-500": !isChecked
                          }
                        )}
                        htmlFor={_value + name}
                      >
                        <input
                          ref={ref}
                          name={name}
                          type="radio"
                          value={_value.toLowerCase()}
                          id={_value + name}
                          onChange={() => {
                            onChange(_value);
                            if (typeof _onChange === "function") _onChange(_value);
                          }}
                          onBlur={onBlur}
                          disabled={disabled}
                          readOnly={readOnly}
                          checked={isChecked}
                          hidden
                        />
                        {/* <span className="checkmark" /> */}
                        {isChecked ? (
                          <FillCheckIcon strokeColor="text-green-600 w-5 h-5" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-300 group-hover:bg-gray-100" />
                        )}
                        <p className={classNames("ml-3 capitalize")}>{_label}</p>
                      </label>
                    );
                  })}
                </div>
                {hint ? <p className="text-sm font-light italic text-gray-500 mb-2">{hint}</p> : null}
                {error ? <FormError error={error.message}/> : null}
              </Fragment>
            );
          }}
        />
      </div>
    );
  }
  if (type === "card") {
    return (
      <div className={classes}>
        <div className="mb-2">
          {title ? (
            <FormLabel id={name} htmlFor={name} className={titleClasses}>
              {title}
            </FormLabel>
          ) : null}
          {subTitle ? <p className={`text-sm font-light text-gray-600 ${subTitleClasses}`}>{subTitle}</p> : null}
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, name, ref, value: selectedValue }, fieldState: { error } }) => {
            return (
              <Fragment>
                <div className={`grid grid-cols-${column} gap-5`}>
                  {options.map((option, idx) => {
                    let _label = "";
                    let _value = "";

                    if (typeof option === "object" && typeof option.label === "string") {
                      _label = option.label;
                      _value = option.value.toLowerCase();
                    }

                    const isChecked = _value === selectedValue?.toLowerCase();
                    return (
                      <label
                        key={+idx}
                        className={classNames(
                          "text-gray-700 text-left leading-snug h-11 flex items-center max-w-sm justify-start px-2 border-2 rounded-[10px]",
                          {
                            "bg-[#f2f2f2] hover:bg-[#dedede]": theme === "light",
                            "bg-white hover:bg-[#fafafa]": theme === "dark",
                            "border-green-600": isChecked,
                            "border-[#f2f2f2] hover:border-gray-500": !isChecked
                          }
                        )}
                        htmlFor={_value + name}
                      >
                        <input
                          ref={ref}
                          name={name}
                          type="radio"
                          value={_value}
                          id={_value + name}
                          onChange={() => {
                            onChange(_value);
                            if (typeof _onChange === "function") _onChange(_value);
                          }}
                          onBlur={onBlur}
                          disabled={disabled}
                          readOnly={readOnly}
                          hidden={true}
                          checked={_value === selectedValue?.toLowerCase()}
                        />

                        {isChecked ? (
                          <FillCheckIcon strokeColor="text-green-600 w-5 h-5" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-300" />
                        )}
                        <p className={classNames("ml-3 capitalize")}>{_label}</p>
                      </label>
                    );
                  })}
                </div>
                {hint ? <p className="text-sm font-light italic text-gray-600">{hint}</p> : null}
                {error ? <FormError error={error.message}/> : null}
              </Fragment>
            );
          }}
        />
      </div>
    );
  }
  if (type === "card-brief") {
    return (
      <div className={classes}>
        <p className="text-base font-semibold mb-2">{title}</p>
        {subTitle ? <p className="text-sm font-light italic text-gray-500 mb-2">{subTitle}</p> : null}
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
            <Fragment>
              <div className={`grid grid-cols-${column} gap-5`}>
                {options.map((option, idx) => {
                  let _label = {} as { img: string; title: string; desc: string };
                  let _value = "";

                  if (typeof option === "object" && typeof option.label === "object") {
                    _label = option.label;
                    _value = option.value.toLowerCase();
                  }

                  return (
                    <article key={+idx} className="col-span-2 md:col-span-1">
                      <input
                        type="radio"
                        name={name}
                        ref={ref}
                        value={_value}
                        id={_value + name}
                        disabled={disabled}
                        readOnly={readOnly}
                        hidden
                        onChange={() => {
                          onChange(_value);
                          if (typeof _onChange === "function") _onChange(_value);
                        }}
                        onBlur={onBlur}
                      />
                      <label
                        htmlFor={_value + name}
                        className={classNames(
                          "text-gray-700 text-left leading-snug h-24 flex items-center max-w-sm justify-start px-2 rounded-lg",
                          {
                            "border-green-600 border-2": value === _value,
                            "bg-[#f2f2f2] hover:bg-gray-100": theme === "light",
                            "bg-white hover:bg-gray-50": theme === "dark"
                          }
                        )}
                      >
                        <div className="w-1/6">
                          <img src={_label.img} className="w-full h-full p-1.5" alt={_label.title} />
                        </div>
                        <div className="w-5/6 pl-2">
                          <p className="font-semibold">{_label.title}</p>
                          <p className="text-sm text-gray-500">{_label.desc}</p>
                        </div>
                      </label>
                    </article>
                  );
                })}
              </div>

              {error ? <FormError error={error.message}/> : null}
            </Fragment>
          )}
        />
      </div>
    );
  }
  if (type === "card-list") {
    return (
      <div className={classes}>
        <div className="mb-2">
          {title ? (
            <FormLabel id={name} htmlFor={name} className={titleClasses}>
              {title}
            </FormLabel>
          ) : null}
          {subTitle ? <p className="text-sm font-light text-gray-600">{subTitle}</p> : null}
        </div>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
            <Fragment>
              <div className="grid grid-cols-1 gap-5">
                {options.map((option, idx) => {
                  let _label = "";
                  let _value = "";

                  if (typeof option === "object" && typeof option.label === "string") {
                    _label = option.label;
                    _value = option.value.toLowerCase();
                  }

                  return (
                    <Fragment key={+idx}>
                      <input
                        type="radio"
                        name={name}
                        ref={ref}
                        value={_value}
                        id={_value + name}
                        disabled={disabled}
                        readOnly={readOnly}
                        hidden
                        onChange={() => {
                          onChange(_value);
                          if (typeof _onChange === "function") _onChange(_value);
                        }}
                        onBlur={onBlur}
                      />
                      <label
                        htmlFor={_value + name}
                        className={classNames(
                          "text-gray-700 text-left leading-snug p-4 md:h-24 md:p-2 flex items-center w-full bg-white shadow-sm justify-start px-2 rounded-lg",
                          {
                            "border-green border-2": value === _value,
                            "border-2 border-white hover:border-gray": value !== _value
                          }
                        )}
                      >
                        <div className="w-16 flex justify-center items-center border-r-2 border-gray-400">
                          <div
                            className={classNames(
                              "w-10 h-10 flex justify-center items-center text-2xl font-bold text-white bg-gray-400 rounded-full",
                              {
                                "bg-green": value === _value
                              }
                            )}
                          >
                            {idx + 1}
                          </div>
                        </div>
                        <div className="w-[calc(100%_-_4rem)] px-3">
                          <p>{_label}</p>
                        </div>
                      </label>
                    </Fragment>
                  );
                })}
              </div>

              {error ? <FormError error={error.message}/> : null}
            </Fragment>
          )}
        />
      </div>
    );
  }

  return null;
}
